import { Box, ListItem, Stack, UnorderedList } from '@chakra-ui/react';
import { Heading, Paragraph, TextLink } from 'ui-components';
import DocumentTitle from '~/components/DocumentTitle';
import LayoutAnonymous from '~/components/LayoutAnonymous';
import { CONTACT_EMAIL } from '~/constants';
import { SITE_URL } from '~/utils';

const PrivacyPage = () => (
  <LayoutAnonymous>
    <Box padding={5}>
      <DocumentTitle>Foam Privacy Policy</DocumentTitle>
      <Heading marginBottom={8}>Foam Privacy Policy</Heading>
      <Stack spacing={6}>
        <Box>
          <Paragraph>
            <Paragraph as="span" fontWeight="medium">
              Whalar ("We") are committed to protecting and respecting your
              privacy.{' '}
            </Paragraph>
            We strive to uphold the highest standards of data privacy to ensure
            your information is always protected.
          </Paragraph>
          <Paragraph>
            <Paragraph as="span" fontWeight="medium">
              This policy
            </Paragraph>{' '}
            (together with our terms of use{' '}
            <TextLink href={`${SITE_URL}/terms`} size="lg">
              https://app.usefoam.com/terms
            </TextLink>{' '}
            and any other documents referred to by it){' '}
            <Paragraph as="span" fontWeight="medium">
              sets out the basis on which any personal data we collect from you,
              or that you provide to us, will be processed by us.
            </Paragraph>{' '}
            Please read the following carefully to understand our views and
            practices regarding your personal data and how we will treat it. By
            visiting{' '}
            <TextLink href={`${SITE_URL}`} size="lg">
              https://app.usefoam.com
            </TextLink>{' '}
            you are accepting and consenting to the practices described in this
            policy.
          </Paragraph>
        </Box>
        <Box>
          <Heading as="h2" size="md">
            Building Trust in the Creator Economy
          </Heading>
          <Paragraph>
            We are committed to:
            <UnorderedList paddingLeft={10} marginTop={4}>
              <ListItem>
                <Paragraph as="span" fontWeight="medium">
                  Never Selling Your Data:
                </Paragraph>{' '}
                We want to be unequivocally clear: Foam will never sell your
                data to anyone. Your insights are your competitive advantage –
                and we believe in protecting that edge.
              </ListItem>
              <ListItem>
                <Paragraph as="span" fontWeight="medium">
                  Data Sharing is Your Choice:
                </Paragraph>{' '}
                Your data is for your agency's eyes only. Unless you grant
                permission or share your data using Foam tools, we will never
                share your information with other agencies, brands, or third
                parties. While Foam offers talent visibility options for Whalar
                casting (which you control), this does not change our commitment
                to keeping your core data confidential.
              </ListItem>
              <ListItem>
                <Paragraph as="span" fontWeight="medium">
                  Your Data, Contained Within Foam:
                </Paragraph>{' '}
                We use data to fine-tune Foam, making it an even more powerful
                tool for talent management.
              </ListItem>
            </UnorderedList>
          </Paragraph>
        </Box>
        <Box>
          <Heading as="h2" size="md">
            How We Utilize Your Data:
          </Heading>
          <Paragraph>
            <UnorderedList paddingLeft={10} marginTop={4}>
              <ListItem>
                <Paragraph as="span" fontWeight="medium">
                  A Better Foam, A Better Experience:
                </Paragraph>{' '}
                By analyzing anonymized platform usage, we can improve features,
                personalize dashboards, and streamline your workflows – all to
                help you and your talent thrive.
              </ListItem>
              <ListItem>
                <Paragraph as="span" fontWeight="medium">
                  Data Security is Non-Negotiable:
                </Paragraph>{' '}
                We employ industry-standard security practices to safeguard your
                data at all times. From encryption to access controls, we take
                every measure to ensure your information remains confidential
                and protected from unauthorized access.
              </ListItem>
              <ListItem>
                <Paragraph as="span" fontWeight="medium">
                  Confidentiality is Key:
                </Paragraph>{' '}
                Your data is accessible only to authorized individuals within
                your agency. We enforce strict access controls and permissions.
              </ListItem>
            </UnorderedList>
          </Paragraph>
        </Box>
        <Box>
          <Heading as="h2" size="md">
            Information We May Collect From You
          </Heading>
          <Paragraph>
            When you visit our sites or use our services, we collect personal
            data. The information we collect about you depends on the services
            that you use (and how you use them). We may collect and process the
            following data about you:
            <UnorderedList paddingLeft={10} marginTop={4}>
              <ListItem>
                <Paragraph as="span" fontWeight="medium">
                  Information You Give Us:
                </Paragraph>{' '}
                This includes information you provide when you register to use
                our site, subscribe to our services, interact and collaborate on
                our site, participate in discussions or if you report a problem.
              </ListItem>
              <ListItem>
                <Paragraph as="span" fontWeight="medium">
                  Information We Collect About You:
                </Paragraph>{' '}
                This may include technical information about your device,
                information about your visit to our site, and how you interact
                with our services and integration partners.
              </ListItem>
              <ListItem>
                <Paragraph as="span" fontWeight="medium">
                  Information We Receive From Other Sources:
                </Paragraph>{' '}
                This may include information from other websites we operate,
                business partners, sub-contractors, and other third parties we
                work with such as Instagram, TikTok, YouTube, and Snap.
              </ListItem>
            </UnorderedList>
          </Paragraph>
        </Box>
        <Box>
          <Heading as="h2" size="md">
            Cookies
          </Heading>
          <Paragraph>
            Our website uses cookies to distinguish you from other users and
            improve our site. More detailed information on the cookies we use
            and the purposes for which we use them are set out at{' '}
            <TextLink href={`${SITE_URL}/terms`} size="lg">
              https://app.usefoam.com/terms.
            </TextLink>{' '}
          </Paragraph>
        </Box>

        <Box>
          <Heading as="h2" fontWeight="medium" size="md">
            Uses Made of the Information
          </Heading>
          <Paragraph>
            We use information held about you in the following ways:
            <UnorderedList paddingLeft={10} marginTop={4}>
              <ListItem>
                <Paragraph as="span" fontWeight="medium">
                  To provide and improve our services:
                </Paragraph>{' '}
                This includes fulfilling your requests, personalizing your
                experience, and ensuring that content is presented effectively.
              </ListItem>
              <ListItem>
                <Paragraph as="span" fontWeight="medium">
                  To communicate with you:
                </Paragraph>{' '}
                This includes service updates, notifications, and marketing
                communications.
              </ListItem>
              <ListItem>
                <Paragraph as="span" fontWeight="medium">
                  To ensure the security and integrity of our services:
                </Paragraph>{' '}
                This includes troubleshooting, data analysis, and protecting
                against fraud.
              </ListItem>
              <ListItem>
                <Paragraph as="span" fontWeight="medium">
                  Product research and development:
                </Paragraph>{' '}
                We use anonymized data to develop, test, and improve our
                products.
              </ListItem>
            </UnorderedList>
          </Paragraph>
        </Box>

        <Box>
          <Heading as="h2" size="md">
            Disclosure of Your Information
          </Heading>
          <Paragraph>
            We may share your personal information within our group, which means
            our subsidiaries and our ultimate holding company and its
            subsidiaries. We may also share your information with selected third
            parties including:
            <UnorderedList paddingLeft={10} marginY={4}>
              <ListItem>
                <Paragraph as="span" fontWeight="medium">
                  Business partners, suppliers and sub-contractors
                </Paragraph>{' '}
                for the performance of any contract we enter into with them or
                you.
              </ListItem>
              <ListItem>
                <Paragraph as="span" fontWeight="medium">
                  Analytics and search engine providers
                </Paragraph>{' '}
                that assist us in the improvement and optimization of our site.
              </ListItem>
            </UnorderedList>
            We may disclose your personal information to third parties in
            limited circumstances, such as:
            <UnorderedList paddingLeft={10} marginY={4}>
              <ListItem>
                <Paragraph as="span" fontWeight="medium">
                  If Whalar Limited or its assets are acquired by a third party.
                </Paragraph>{' '}
              </ListItem>
              <ListItem>
                <Paragraph as="span" fontWeight="medium">
                  If we are under a duty to disclose your data to comply with
                  legal obligations.
                </Paragraph>{' '}
              </ListItem>
            </UnorderedList>
            <Paragraph as="span" fontWeight="medium">
              We will never sell your data to third-party advertisers or
              advertising networks.
            </Paragraph>
          </Paragraph>
        </Box>

        <Box>
          <Heading as="h2" size="md">
            Where We Store Your Personal Data
          </Heading>
          <Paragraph>
            The data that we collect from you may be transferred to, and stored
            at, a destination outside the European Economic Area ("EEA"). By
            submitting your personal data, you agree to this transfer, storing
            or processing. We will take all steps reasonably necessary to ensure
            that your data is treated securely and in accordance with this
            privacy policy.
          </Paragraph>
        </Box>
        <Box>
          <Heading as="h2" size="md">
            Deletion and Retention
          </Heading>
          <Paragraph>
            You can request the removal of personal information we hold about
            you at any time by emailing{' '}
            <TextLink href={`mailto:${CONTACT_EMAIL}`}>
              {CONTACT_EMAIL}
            </TextLink>{' '}
            and requesting that your account be deleted. However, we may keep
            some of your personal information for as long as reasonably
            necessary for legitimate business interests such as reporting and
            payment processing for campaigns you or your talent have
            participated in for the purposes of fulfilling the contractual
            obligations of the contracts you choose to enter into.
          </Paragraph>
        </Box>
        <Box>
          <Heading as="h2" size="md">
            Your Rights
          </Heading>
          <Paragraph>
            You have certain rights in respect to your personal information,
            including the right to access, correct, or delete your personal
            data.
          </Paragraph>
        </Box>
        <Box>
          <Heading as="h2" size="md">
            Changes to our privacy policy
          </Heading>
          <Paragraph>
            Any changes we may make to our privacy policy in the future will be
            posted on this page.
          </Paragraph>
        </Box>
        <Box>
          <Heading as="h2" size="md">
            Contact
          </Heading>
          <Paragraph>
            Questions, comments and requests regarding this privacy policy are
            welcomed and should be addressed to{' '}
            <TextLink href={`mailto:${CONTACT_EMAIL}`} size="md">
              {CONTACT_EMAIL}
            </TextLink>
          </Paragraph>
        </Box>
      </Stack>
    </Box>
  </LayoutAnonymous>
);

export default PrivacyPage;
